import React, { useCallback, useEffect, useState } from 'react';
import { $getSelection, $isRangeSelection } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isCodeNode, CodeNode } from '@lexical/code';
import { $getNearestNodeOfType } from '@lexical/utils';
import './CodeBlockLanguagePicker.css';

/**
 * KAP-306 — language picker for the active fenced code block.
 *
 * Anchored next to the V2 composer's code blocks. Reads the current
 * CodeNode.language whenever the selection moves into a code block and
 * lets the user pick from a curated list (or type a custom value).
 *
 * Round-trip behavior: language hint serializes via
 * `$convertToMarkdownString(TRANSFORMERS)` as the fence info string
 * (e.g. ` ```ts ` … ` ``` `), satisfying KAP-306 acceptance "code block
 * with `ts` language hint survives round-trip."
 */

const COMMON_LANGUAGES = [
  { value: 'plaintext', label: 'Plain text' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'tsx', label: 'TSX' },
  { value: 'python', label: 'Python' },
  { value: 'json', label: 'JSON' },
  { value: 'sql', label: 'SQL' },
  { value: 'bash', label: 'Bash' },
  { value: 'css', label: 'CSS' },
  { value: 'html', label: 'HTML' },
  { value: 'markdown', label: 'Markdown' }
];

function CodeBlockLanguagePicker() {
  const [editor] = useLexicalComposerContext();
  const [activeCodeKey, setActiveCodeKey] = useState(null);
  const [language, setLanguage] = useState('');

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          setActiveCodeKey(null);
          return;
        }
        const anchorNode = selection.anchor.getNode();
        const codeNode = $getNearestNodeOfType(anchorNode, CodeNode);
        if (codeNode && $isCodeNode(codeNode)) {
          setActiveCodeKey(codeNode.getKey());
          setLanguage(codeNode.getLanguage() || '');
        } else {
          setActiveCodeKey(null);
        }
      });
    });
  }, [editor]);

  const handleChange = useCallback(
    (event) => {
      const value = event.target.value;
      setLanguage(value);
      if (!activeCodeKey) return;
      editor.update(() => {
        const node = editor.getEditorState()._nodeMap.get(activeCodeKey);
        if (node && $isCodeNode(node)) {
          node.setLanguage(value || null);
        }
      });
    },
    [editor, activeCodeKey]
  );

  if (!activeCodeKey) return null;

  return (
    <div className="msg-composer-code-lang" role="group" aria-label="Code block language">
      <label className="msg-composer-code-lang-label" htmlFor="msg-composer-code-lang-select">
        Lang
      </label>
      <select
        id="msg-composer-code-lang-select"
        className="msg-composer-code-lang-select"
        value={COMMON_LANGUAGES.some((l) => l.value === language) ? language : '__custom'}
        onChange={(event) => {
          if (event.target.value === '__custom') return;
          handleChange(event);
        }}
      >
        <option value="">(none)</option>
        {COMMON_LANGUAGES.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
        {language && !COMMON_LANGUAGES.some((l) => l.value === language) ? (
          <option value="__custom">{language} (custom)</option>
        ) : null}
      </select>
      <input
        type="text"
        className="msg-composer-code-lang-input"
        placeholder="custom"
        aria-label="Custom language hint"
        value={COMMON_LANGUAGES.some((l) => l.value === language) ? '' : language}
        onChange={handleChange}
      />
    </div>
  );
}

export default CodeBlockLanguagePicker;
